import * as THREE from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';

const DPR_CAP = 2;
const REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function corrugatedTexture(dark) {
  const c = document.createElement('canvas');
  c.width = 1024; c.height = 512;
  const x = c.getContext('2d');
  x.fillStyle = dark ? '#34353b' : '#8a6a55';
  x.fillRect(0, 0, c.width, c.height);
  for (let i = 0; i < c.width; i += 7) {
    x.fillStyle = 'rgba(255,255,255,0.028)';
    x.fillRect(i, 0, 1, c.height);
    x.fillStyle = 'rgba(0,0,0,0.05)';
    x.fillRect(i + 3, 0, 2, c.height);
  }
  for (let i = 0; i < 14000; i++) {
    const a = Math.random() * 0.05;
    x.fillStyle = 'rgba(' + (Math.random() > 0.5 ? '255,255,255' : '0,0,0') + ',' + a.toFixed(3) + ')';
    x.fillRect(Math.random() * c.width, Math.random() * c.height, 2, 2);
  }
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.anisotropy = 4;
  return t;
}

function bumpTexture() {
  const c = document.createElement('canvas');
  c.width = 512; c.height = 256;
  const x = c.getContext('2d');
  x.fillStyle = '#808080';
  x.fillRect(0, 0, c.width, c.height);
  for (let i = 0; i < c.width; i += 6) {
    const g = x.createLinearGradient(i, 0, i + 6, 0);
    g.addColorStop(0, '#6d6d6d'); g.addColorStop(0.5, '#9a9a9a'); g.addColorStop(1, '#6d6d6d');
    x.fillStyle = g;
    x.fillRect(i, 0, 6, c.height);
  }
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  return t;
}

class MovdBox3D extends HTMLElement {
  connectedCallback() {
    if (this.__init) return;
    this.__init = true;
    this.mode = this.getAttribute('mode') || 'hero';
    this.style.display = 'block';
    this.style.position = 'relative';
    this.style.touchAction = 'pan-y';

    try {
      const probe = document.createElement('canvas');
      if (!(probe.getContext('webgl2') || probe.getContext('webgl'))) throw new Error('no webgl');
    } catch (e) {
      // WebGL neni k dispozici - necháme fallback obrázek (skrytý jen
      // přes movd-box-3d:not(:defined) v CSS) viditelný, element uz je
      // :defined takze by CSS pravidlo prestalo platit samo, ale pro
      // jistotu nic neskryvame a rovnou koncime.
      return;
    }

    Array.from(this.children).forEach(n => { n.style.display = 'none'; });
    this.build();
  }

  build() {
    const W = this.clientWidth || 480, H = this.clientHeight || 380;
    const renderer = this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true, powerPreference: 'high-performance' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, DPR_CAP));
    renderer.setSize(W, H);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.42;
    renderer.domElement.style.display = 'block';
    renderer.domElement.style.width = '100%';
    renderer.domElement.style.height = '100%';
    renderer.domElement.style.cursor = 'grab';
    this.appendChild(renderer.domElement);

    const scene = this.scene = new THREE.Scene();
    const camera = this.camera = new THREE.PerspectiveCamera(32, W / H, 0.1, 100);
    camera.position.set(0, 3.4, 12.6);
    camera.lookAt(0, 0.2, 0);

    const pmrem = new THREE.PMREMGenerator(renderer);
    scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    scene.environmentIntensity = 0.5;
    scene.add(new THREE.HemisphereLight(0xf4f0e8, 0x2a251f, 0.9));
    const key = new THREE.DirectionalLight(0xfff6e8, 3.4);
    key.position.set(5.5, 9, 6.5);
    key.castShadow = true;
    key.shadow.mapSize.set(1024, 1024);
    key.shadow.camera.near = 1; key.shadow.camera.far = 30;
    key.shadow.camera.left = -8; key.shadow.camera.right = 8;
    key.shadow.camera.top = 8; key.shadow.camera.bottom = -8;
    key.shadow.bias = -0.0012;
    key.shadow.radius = 3;
    scene.add(key);
    const fill = new THREE.DirectionalLight(0xd9cdbd, 1.25);
    fill.position.set(-6, 3.5, 4);
    scene.add(fill);
    const rim = new THREE.DirectionalLight(0xffffff, 0.95);
    rim.position.set(-2, 4, -7);
    scene.add(rim);

    const ground = new THREE.Mesh(new THREE.PlaneGeometry(40, 40), new THREE.ShadowMaterial({ opacity: 0.34 }));
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -1.52;
    ground.receiveShadow = true;
    scene.add(ground);

    const rig = this.rig = new THREE.Group();
    scene.add(rig);
    const spin = this.spin = new THREE.Group();
    rig.add(spin);

    const BW = 5.5, BH = 3.0, BD = 4.0;
    const card = corrugatedTexture(true);
    const bump = bumpTexture();
    bump.repeat.set(8, 4);
    const bodyMat = new THREE.MeshStandardMaterial({
      color: 0x33343a, map: card, bumpMap: bump, bumpScale: 0.014,
      roughness: 0.86, metalness: 0.04
    });
    const body = new THREE.Mesh(new THREE.BoxGeometry(BW, BH, BD, 1, 1, 1), bodyMat);
    body.castShadow = true; body.receiveShadow = true;
    body.position.y = -1.52 + BH / 2;
    spin.add(body);

    const edgeMat = new THREE.MeshStandardMaterial({ color: 0x35363c, roughness: 0.85 });
    const topRim = new THREE.Mesh(new THREE.BoxGeometry(BW + 0.012, 0.055, BD + 0.012), edgeMat);
    topRim.position.y = body.position.y + BH / 2 - 0.03;
    spin.add(topRim);

    const loader = new THREE.TextureLoader();
    const logoTex = loader.load(window.MOVD_BOX_LOGO_URL || 'assets/logo-white.png', t => { t.colorSpace = THREE.SRGBColorSpace; this.needs = true; });
    logoTex.anisotropy = 8;

    const decalMat = new THREE.MeshStandardMaterial({
      color: 0xf4f0e8, map: logoTex, transparent: true, roughness: 0.62, metalness: 0,
      depthWrite: false, polygonOffset: true, polygonOffsetFactor: -3, opacity: 0.97
    });
    const lw = 2.5, lh = lw * (468 / 2241);
    const front = new THREE.Mesh(new THREE.PlaneGeometry(lw, lh), decalMat);
    front.position.set(-0.55, body.position.y - 0.62, BD / 2 + 0.005);
    spin.add(front);

    const side = new THREE.Mesh(new THREE.PlaneGeometry(lw * 0.62, lh * 0.62), decalMat);
    side.rotation.y = Math.PI / 2;
    side.position.set(BW / 2 + 0.005, body.position.y - 0.55, -0.35);
    spin.add(side);

    const topLogo = new THREE.Mesh(new THREE.PlaneGeometry(lw * 0.78, lh * 0.78), decalMat);
    topLogo.rotation.x = -Math.PI / 2;
    topLogo.position.set(-0.5, body.position.y + BH / 2 + 0.006, 1.05);
    spin.add(topLogo);

    const tapeMat = new THREE.MeshStandardMaterial({ color: 0x8a6a55, roughness: 0.55, map: corrugatedTexture(false), transparent: true });
    this.tapeMat = tapeMat;
    const tapeTopY = body.position.y + BH / 2 + 0.066;
    this.tapeTopY = tapeTopY;
    const tapeZ = -0.9;
    const tapeHalfLen = (BW + 0.02) / 2;

    const tapeHingeL = new THREE.Group();
    tapeHingeL.position.set(0, tapeTopY, tapeZ);
    const tapeLMesh = new THREE.Mesh(new THREE.BoxGeometry(tapeHalfLen, 0.02, 0.72), tapeMat);
    tapeLMesh.position.set(-tapeHalfLen / 2, 0, 0);
    tapeHingeL.add(tapeLMesh);
    spin.add(tapeHingeL);
    this.tapeHingeL = tapeHingeL;

    const tapeHingeR = new THREE.Group();
    tapeHingeR.position.set(0, tapeTopY, tapeZ);
    const tapeRMesh = new THREE.Mesh(new THREE.BoxGeometry(tapeHalfLen, 0.02, 0.72), tapeMat);
    tapeRMesh.position.set(tapeHalfLen / 2, 0, 0);
    tapeHingeR.add(tapeRMesh);
    spin.add(tapeHingeR);
    this.tapeHingeR = tapeHingeR;

    const tapeSideL = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.5, 0.72), tapeMat);
    tapeSideL.position.set(-BW / 2 - 0.002, body.position.y + BH / 2 - 0.25, -0.9);
    spin.add(tapeSideL);
    const tapeSideR = tapeSideL.clone();
    tapeSideR.position.x = BW / 2 + 0.002;
    spin.add(tapeSideR);
    this.tapeSideL = tapeSideL;
    this.tapeSideR = tapeSideR;

    const tearMat = new THREE.MeshStandardMaterial({ color: 0xd8cfbd, roughness: 0.7 });
    const tear = new THREE.Mesh(new THREE.BoxGeometry(BW + 0.015, 0.1, 0.015), tearMat);
    tear.position.set(0, body.position.y + 0.62, BD / 2 + 0.006);
    spin.add(tear);

    const lidMat = new THREE.MeshStandardMaterial({ color: 0x35363c, map: card, bumpMap: bump, bumpScale: 0.01, roughness: 0.9, side: THREE.DoubleSide });
    const lidHinge = new THREE.Group();
    lidHinge.position.set(0, body.position.y + BH / 2, -BD / 2);
    const lid = new THREE.Mesh(new THREE.BoxGeometry(BW - 0.04, 0.06, BD - 0.02), lidMat);
    lid.position.set(0, 0.03, (BD - 0.02) / 2);
    lid.castShadow = true;
    lidHinge.add(lid);
    spin.add(lidHinge);
    this.lidHinge = lidHinge;

    const contents = this.contents = new THREE.Group();
    contents.position.y = body.position.y - 0.35;
    spin.add(contents);
    const palette = [0xede7da, 0xcbbda8, 0x2e2f32, 0xd8d3c6, 0xb9a38a, 0x8a6a55, 0xe3dccf, 0x9a9490];
    let n = 0;
    for (let ix = 0; ix < 4; ix++) {
      for (let iz = 0; iz < 3; iz++) {
        const w = 1.0 + Math.random() * 0.22, h = 0.7 + Math.random() * 0.75, dd = 0.9 + Math.random() * 0.25;
        const m = new THREE.Mesh(
          new THREE.BoxGeometry(w, h, dd),
          new THREE.MeshStandardMaterial({ color: palette[n % palette.length], roughness: 0.75 })
        );
        m.position.set(-1.95 + ix * 1.3, h / 2 - 0.5, -1.25 + iz * 1.25);
        m.rotation.y = (Math.random() - 0.5) * 0.18;
        m.castShadow = true; m.receiveShadow = true;
        m.userData.baseY = m.position.y;
        contents.add(m);
        n++;
      }
    }
    contents.visible = false;

    this.state = { rot: -0.55, vel: 0, drag: false, lastX: 0, lastY: 0, idle: 0, tiltX: 0, tiltY: 0, mx: 0, my: 0, open: 0, prog: 0 };
    this.bind();

    this.visible = true;
    this.io = new IntersectionObserver(e => { this.visible = e[0].isIntersecting; }, { rootMargin: '150px' });
    this.io.observe(this);

    this.ro = new ResizeObserver(() => this.resize());
    this.ro.observe(this);

    this.clock = new THREE.Clock();
    this.loop = this.loop.bind(this);
    this.raf = requestAnimationFrame(this.loop);
  }

  bind() {
    const el = this.renderer.domElement;
    const s = this.state;
    const down = e => {
      s.drag = true; s.idle = 0;
      s.lastX = e.clientX; s.lastY = e.clientY;
      el.style.cursor = 'grabbing';
      el.setPointerCapture && e.pointerId !== undefined && el.setPointerCapture(e.pointerId);
    };
    const move = e => {
      const r = el.getBoundingClientRect();
      s.mx = ((e.clientX - r.left) / r.width) * 2 - 1;
      s.my = ((e.clientY - r.top) / r.height) * 2 - 1;
      if (!s.drag) return;
      const dx = e.clientX - s.lastX;
      s.vel = dx * 0.006;
      s.rot += dx * 0.006;
      s.lastX = e.clientX; s.lastY = e.clientY;
      s.idle = 0;
    };
    const up = () => { if (s.drag) { s.drag = false; s.idle = 0; el.style.cursor = 'grab'; } };
    el.addEventListener('pointerdown', down);
    el.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    el.addEventListener('pointerleave', () => { s.mx = 0; s.my = 0; });
    this.cleanupEvents = () => {
      el.removeEventListener('pointerdown', down);
      el.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
  }

  scrollProgress() {
    const vh = window.innerHeight || 800;
    const track = this.closest('[data-unbox-track]');
    if (track) {
      const t = track.getBoundingClientRect();
      const denom = Math.max(1, t.height - vh);
      return Math.max(0, Math.min(1, -t.top / denom));
    }
    const r = this.getBoundingClientRect();
    return Math.max(0, Math.min(1, (vh - r.top - vh * 0.22) / (r.height + vh * 0.6)));
  }

  resize() {
    if (!this.renderer) return;
    const W = this.clientWidth || 1, H = this.clientHeight || 1;
    // iOS Safari's URL bar collapsing during scroll changes vh-based
    // element heights continuously, which fires ResizeObserver on every
    // scroll tick. Resizing the WebGL drawing buffer always clears it, so
    // without this guard that showed up as a visible flicker while
    // swiping fast. Skip resizes under a few px and repaint immediately
    // after a real one so there's no blank frame waiting for the next tick.
    if (this.__lastW && Math.abs(this.__lastW - W) < 4 && Math.abs(this.__lastH - H) < 4) return;
    this.__lastW = W; this.__lastH = H;
    this.renderer.setSize(W, H, false);
    this.camera.aspect = W / H;
    this.camera.updateProjectionMatrix();
    if (this.visible) this.renderer.render(this.scene, this.camera);
  }

  loop() {
    this.raf = requestAnimationFrame(this.loop);
    if (!this.visible) return;
    const dt = Math.min(this.clock.getDelta(), 0.05);
    const s = this.state;

    if (this.mode === 'unbox') {
      const target = this.scrollProgress();
      s.prog += (target - s.prog) * Math.min(1, dt * 6);
      const p = s.prog;
      s.rot = -0.6 + p * 1.25;
      const openP = Math.max(0, Math.min(1, (p - 0.12) / 0.68));
      s.open += (openP - s.open) * Math.min(1, dt * 7);
      this.camera.position.z = 12.6 - s.open * 2.6;
      this.camera.position.y = 3.4 + s.open * 0.9;
      this.camera.lookAt(0, 0.1 + s.open * 0.3, 0);
      this.rig.position.y = -0.1 + Math.sin(p * Math.PI) * 0.12;

      const tearEase = Math.min(1, s.open / 0.25);
      const tearSmooth = tearEase * tearEase * (3 - 2 * tearEase);
      if (this.tapeHingeL) {
        this.tapeHingeL.rotation.y = -tearSmooth * 1.9;
        this.tapeHingeL.position.y = this.tapeTopY + tearSmooth * 0.14;
        this.tapeHingeR.rotation.y = tearSmooth * 1.9;
        this.tapeHingeR.position.y = this.tapeTopY + tearSmooth * 0.14;
        this.tapeMat.opacity = 1 - tearSmooth;
        const sideScale = 1 - tearSmooth;
        this.tapeSideL.scale.set(1, sideScale, sideScale);
        this.tapeSideR.scale.set(1, sideScale, sideScale);
        const tapeGone = tearSmooth > 0.97;
        this.tapeHingeL.visible = !tapeGone;
        this.tapeHingeR.visible = !tapeGone;
        this.tapeSideL.visible = !tapeGone;
        this.tapeSideR.visible = !tapeGone;
      }
    } else {
      if (!s.drag) {
        if (REDUCED) { s.rot = -0.55; }
        else {
          s.idle += dt;
          s.vel *= 0.93;
          const auto = s.idle > 1.6 ? Math.min(1, (s.idle - 1.6) / 1.4) : 0;
          s.rot += s.vel + auto * dt * 0.28;
        }
      }
      s.open += (0 - s.open) * Math.min(1, dt * 6);
      this.rig.position.y = REDUCED ? 0 : Math.sin(this.clock.elapsedTime * 0.55) * 0.055;
    }

    const tx = REDUCED ? 0 : s.my * 0.11, ty = REDUCED ? 0 : s.mx * 0.16;
    s.tiltX += (tx - s.tiltX) * Math.min(1, dt * 4);
    s.tiltY += (ty - s.tiltY) * Math.min(1, dt * 4);

    this.spin.rotation.y = s.rot;
    this.rig.rotation.x = 0.03 + s.tiltX;
    this.rig.rotation.y = s.tiltY;

    const o = s.open;
    if (this.lidHinge) {
      this.lidHinge.rotation.x = -o * 2.0;
    }
    if (this.contents) {
      this.contents.visible = o > 0.04;
      this.contents.children.forEach((m, i) => {
        const k = Math.max(0, Math.min(1, (o - 0.28 - (i % 4) * 0.055) / 0.5));
        m.position.y = m.userData.baseY - 0.55 + k * 0.55;
        m.material.opacity = 1;
      });
    }

    this.renderer.render(this.scene, this.camera);
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.raf);
    this.io && this.io.disconnect();
    this.ro && this.ro.disconnect();
    this.cleanupEvents && this.cleanupEvents();
    if (this.renderer) {
      this.scene.traverse(o => {
        if (o.geometry) o.geometry.dispose();
        if (o.material) { [].concat(o.material).forEach(m => { Object.keys(m).forEach(k => { const v = m[k]; if (v && v.isTexture) v.dispose(); }); m.dispose(); }); }
      });
      this.renderer.dispose();
      this.renderer.domElement.remove();
      this.renderer = null;
    }
    this.__init = false;
  }
}

if (!customElements.get('movd-box-3d')) customElements.define('movd-box-3d', MovdBox3D);
